import { Chips } from '@frontend/chips';
import { useOrgData } from '@frontend/get-org-data';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { MultiLocationSelector } from '@frontend/multi-location-selector';
import { OrgLocationPicker } from '@frontend/org-location-picker';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Stepper, Text } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../../../bulk-message-editor.context';
import { useOrgLevelLocationSelector } from '../../../hooks/use-org-level-location-selector';
import { checkIsUnscheduled, toggleChangedIds } from '../../utils/utils';
import { overrideTextStyles, stepValueContainerStyles } from '../styles';
import { LocationIdsFieldProps, Step, StepId } from '../types';

type Props = {
  displayedLocations: string[];
  locationsWithACLAccess: string[];
  locationIdsFieldProps: LocationIdsFieldProps;
  next: (stepId: StepId) => void;
  previous: (stepId: StepId) => void;
  hide: boolean;
};

export const useLocationStep = ({
  displayedLocations,
  locationsWithACLAccess,
  locationIdsFieldProps,
  next,
  previous,
  hide,
}: Props): Step => {
  const { t } = useTranslation('bulk-messaging');
  const { getLocationName } = useOrgData(); // need to use this hook instead of `useAppScopeStore` because not all locations will be always represented in the scope store (e.g. editing a campaign when you only have some locations selected globally)
  const { campaignType, currentStatus, setLocationIds } = useBulkMessageEditorContext();
  const { isOrgLevelSelector, permission } = useOrgLevelLocationSelector(campaignType);
  // TODO: not using the readOnly from the context because we only want to check the status here (not the time-based readOnly)
  // I have a TODO in the utils file to evaluate if we can simplify this logic
  const readOnly = !checkIsUnscheduled(currentStatus); // locations can only be changed if the campaign is one of the unscheduled statuses (doesn't need to check the time)

  const id: StepId = 'locations';

  // The picker emits the full selection, but the field's onChange toggles a SINGLE id at a time — bridge
  // via toggleChangedIds. Also set the context source-of-truth to the exact selection so the effect that
  // mirrors context -> field stays a no-op (otherwise it re-adds removed locations in a loop).
  const handleLocationsChange = (ids: string[]) => {
    const unique = toggleChangedIds(locationIdsFieldProps.value, ids, (locationId) =>
      locationIdsFieldProps.onChange({ name: 'locationIds', value: locationId })
    );
    setLocationIds(unique);
  };

  return {
    id,
    Component: (
      <>
        <Stepper.Title>{t('Select Locations to Send From')}</Stepper.Title>

        <Stepper.Content>
          {isOrgLevelSelector ? (
            <OrgLocationPicker
              permission={permission}
              value={locationIdsFieldProps.value}
              onChange={handleLocationsChange}
              isRequired={!readOnly}
              readOnly={readOnly}
              trackingIdPrefix={BulkEmailPrefixes.Editor}
            />
          ) : (
            <MultiLocationSelector
              accessibleLocations={locationsWithACLAccess}
              displayedLocations={displayedLocations}
              fieldProps={{
                locationIds: { ...locationIdsFieldProps, name: 'locationIds' },
              }}
              isRequired={readOnly ? false : true}
              readOnly={readOnly}
              trackingIdPrefix={BulkEmailPrefixes.Editor}
            />
          )}
          {readOnly && (
            <Text
              size='medium'
              color='light'
              css={{
                display: 'flex',
                alignItems: 'center',
                marginTop: theme.spacing(2),
              }}
            >
              <Icon
                name='info-small'
                color='light'
                css={{
                  display: 'inline-block',
                  marginRight: theme.spacing(0.5),
                }}
              />
              {t('Once a campaign is scheduled, the locations associated with it cannot be changed.')}
            </Text>
          )}
        </Stepper.Content>

        <Stepper.ButtonBarAlternate>
          <Stepper.ButtonAlternate onClick={() => previous(id)} position='secondary' type='none'>
            {t('Previous')}
          </Stepper.ButtonAlternate>
          <Stepper.ButtonAlternate onClick={() => next(id)} position='primary' type='none'>
            {t('Next')}
          </Stepper.ButtonAlternate>
        </Stepper.ButtonBarAlternate>
      </>
    ),
    collapsedValue: (
      <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
        <Text>{t('Select Locations to Send From')}</Text>
        {locationIdsFieldProps.value.length > 0 && (
          <div css={{ display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' }}>
            {...locationIdsFieldProps.value.map((id, index) => (
              <Chips.LocationChip
                key={index + '-' + id}
                css={{
                  ...overrideTextStyles,
                  textTransform: 'initial',
                  fontSize: theme.fontSize(12),
                  '.chip': { maxWidth: 'unset' },
                }}
                variant='primaryDark'
              >
                {getLocationName(id)}
              </Chips.LocationChip>
            ))}
          </div>
        )}
      </div>
    ),
    hide,
  };
};
